import React, { useCallback, useEffect, useState } from "react";
import { T, FAMILY, FONT } from "./theme/tokens.js";
import { Panel, Empty, Notice, Btn } from "./components/ui/index.jsx";
import { isConfigured, supabase, friendlyError } from "./lib/supabase.js";
import { AuthProvider, useAuth } from "./auth/AuthProvider.jsx";
import Setup from "./pages/Setup.jsx";
import SignIn from "./pages/SignIn.jsx";
import NewPassword from "./pages/NewPassword.jsx";
import ContractPicker from "./pages/ContractPicker.jsx";
import AppShell from "./pages/AppShell.jsx";
import Team from "./pages/Team.jsx";
import Contracts from "./pages/Contracts.jsx";
import ContractWorkspace from "./pages/ContractWorkspace.jsx";
import Dashboards from "./pages/Dashboards.jsx";
import Finance from "./pages/Finance.jsx";
import Schedule from "./pages/Schedule.jsx";
import Import from "./pages/Import.jsx";
import Trackers from "./pages/Trackers.jsx";
import Reports from "./pages/Reports.jsx";
import Comms from "./pages/Comms.jsx";
import RegisterTable from "./components/RegisterTable.jsx";
import { REGISTERS } from "./lib/registers.js";

const CONTEXT_KEY = "contractops:context";

function Splash({ text }) {
  return (
    <div style={{
      minHeight: "100vh", background: T.navy, display: "flex", alignItems: "center",
      justifyContent: "center", fontFamily: FAMILY, color: T.sideText,
    }}>
      <style>{FONT}</style>
      {text}
    </div>
  );
}

/* Views that are scoped to exactly one contract. Reaching one of these in
   portfolio mode is not allowed: contract data never blends. */
const SCOPED_VIEWS = ["finance", "schedule", "dashboards", "risks", "actions",
                      "trackers", "import", "reports", "comms"];

/* Sidebar pages that are simply one register, scoped to the active contract. */
const SCOPED_REGISTERS = {
  risks: "risks",
  actions: "actions",
};

function Workspace() {
  const { profile, loading, isAdmin, isOwner, canEdit, signOut, user } = useAuth();
  const [contracts, setContracts] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [contextId, setContextId] = useState(() => localStorage.getItem(CONTEXT_KEY));
  const [view, setView] = useState("overview");
  const [query, setQuery] = useState("");

  /* Row Level Security decides what comes back here. An unassigned member
     receives an empty list from the server, not a filtered one. */
  const loadContracts = useCallback(async () => {
    const { data, error } = await supabase
      .from("contracts")
      .select("*")
      .eq("status", "active")
      .order("name");
    if (error) setLoadError(friendlyError(error));
    else { setContracts(data ?? []); setLoadError(""); }
  }, []);

  useEffect(() => { if (profile) loadContracts(); }, [profile, loadContracts]);

  const chooseContext = (id) => {
    setContextId(id);
    if (id) localStorage.setItem(CONTEXT_KEY, id);
    else localStorage.removeItem(CONTEXT_KEY);
  };

  if (loading || (profile && contracts === null)) return <Splash text="Loading workspace..." />;
  if (!profile) {
    return (
      <Splash text={`Signed in as ${user?.email}, but no profile row exists. Ask Heidi to check the account.`} />
    );
  }

  const visible = contracts;
  const validContext = contextId === "all"
    ? isAdmin
    : visible.some((c) => c.id === contextId);

  /* First screen after sign-in when more than one contract is reachable. */
  if (!validContext && (visible.length > 1 || (isAdmin && visible.length > 0))) {
    return (
      <ContractPicker
        contracts={visible}
        isAdmin={isAdmin}
        firstName={(profile.full_name || profile.email).split(" ")[0]}
        onChoose={chooseContext}
        onSignOut={signOut}
      />
    );
  }

  const effectiveContext = validContext
    ? contextId
    : (visible[0]?.id ?? null);
  const activeContract = visible.find((c) => c.id === effectiveContext) ?? null;
  const inScope = activeContract ? [activeContract] : visible;

  const body = () => {
    if (loadError) return <Notice kind="error">{loadError}</Notice>;

    if (view === "team") {
      return <Team contracts={visible} isAdmin={isAdmin} isOwner={isOwner} me={profile} />;
    }
    if (view === "contracts") {
      if (activeContract) {
        return <ContractWorkspace contract={activeContract} onReloadContracts={loadContracts} />;
      }
      return (
        <Contracts
          contracts={inScope}
          query={query}
          onCreated={async (id) => { await loadContracts(); chooseContext(id); }}
        />
      );
    }
    if (!activeContract && SCOPED_VIEWS.includes(view)) {
      return (
        <Panel title="One contract at a time">
          <Empty text="This page shows a single contract. Pick one from the switcher above." />
        </Panel>
      );
    }
    if (visible.length === 0) {
      return (
        <Panel title={isAdmin ? "No contracts yet" : "No contracts assigned to you yet"}>
          <div style={{ textAlign: "center", padding: 24 }}>
            <div style={{ fontSize: 14, color: T.soft, marginBottom: 16, lineHeight: 1.6 }}>
              {isAdmin
                ? "Create a contract to begin. Every new contract is seeded with the USVI baseline risk register and the standard contracts compliance checklist."
                : "Ask Heidi to assign you to a contract on the Team page, or create one yourself."}
            </div>
            <Btn kind="gold" onClick={() => setView("contracts")}>Go to contracts</Btn>
          </div>
        </Panel>
      );
    }

    if (view === "dashboards" && activeContract) {
      return <Dashboards contract={activeContract} onReloadContracts={loadContracts} />;
    }
    if (view === "finance" && activeContract) {
      return <Finance contract={activeContract} />;
    }
    if (view === "schedule" && activeContract) {
      return <Schedule contract={activeContract} />;
    }
    if (view === "trackers" && activeContract) {
      return (
        <Trackers contract={activeContract} onGoToImport={() => setView("import")}
          onReloadContracts={loadContracts} />
      );
    }
    if (view === "comms" && activeContract) {
      return <Comms contract={activeContract} />;
    }
    if (view === "reports" && activeContract) {
      return <Reports contract={activeContract} />;
    }
    /* Hidden from the sidebar for a viewer, and refused here too. A page
       reached by typing its name into the address bar is still a page. */
    if (view === "import" && activeContract) {
      return canEdit
        ? <Import contract={activeContract} onGoToTrackers={() => setView("trackers")} />
        : <Empty text="You have view access to this workspace, so importing is not available to you." />;
    }

    /* Registers scoped to exactly one contract. Contract data never blends. */
    if (activeContract && SCOPED_REGISTERS[view]) {
      const key = SCOPED_REGISTERS[view];
      return (
        <Panel title={`${REGISTERS[key].label} · ${activeContract.name}`}>
          <RegisterTable registerKey={key} contract={activeContract} />
        </Panel>
      );
    }

    if (view === "overview" && activeContract) {
      return <ContractWorkspace contract={activeContract} onReloadContracts={loadContracts} />;
    }

    if (view === "overview" && !activeContract) {
      return (
        <Panel title="All contracts (portfolio)">
          <Empty text="The executive portfolio view arrives in a later phase. Pick a contract from the switcher to work in it." />
        </Panel>
      );
    }

    return (
      <Panel title={activeContract ? activeContract.name : "All contracts"}>
        <Empty text="This section arrives in a later phase, on top of the registers now in place." />
      </Panel>
    );
  };

  return (
    <AppShell
      view={view}
      onView={setView}
      profile={profile}
      contracts={visible}
      activeContract={activeContract}
      isAdmin={isAdmin}
      canEdit={canEdit}
      onSwitchContext={chooseContext}
      onSignOut={signOut}
      query={query}
      onQuery={setQuery}
    >
      {body()}
    </AppShell>
  );
}

function Gate() {
  const { session, loading, recovering, mustSetPassword } = useAuth();
  if (loading) return <Splash text="Loading workspace..." />;
  if (!session) return <SignIn />;
  /* Arrived on a password reset link. Nothing else until a password is set,
     otherwise the reset never happens and they are locked out again. */
  /* Two ways to land here: a reset link, or a password Heidi issued. Both
     mean the same thing, which is that no password of their own exists yet. */
  if (recovering || mustSetPassword) return <NewPassword />;
  return <Workspace />;
}

export default function App() {
  if (!isConfigured) return <Setup />;
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
